(() => {
  'use strict';

  const clamp = (value, min = 0, max = 1) => Math.min(max, Math.max(min, value));

  function formatMoney(cents, currency) {
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency: currency || 'GBP'
      }).format(Number(cents || 0) / 100);
    } catch (error) {
      return `${(Number(cents || 0) / 100).toFixed(2)} ${currency || ''}`.trim();
    }
  }

  function initScrollStory(root) {
  const story = root.querySelector('[data-jwl-scroll-story]');

  if (!story) return;

  const video = story.querySelector('video');
  const steps = [
    ...story.querySelectorAll('[data-jwl-story-step]')
  ];

  const mode = story.dataset.jwlVideoMode || 'scroll';

  const reduceMotion = window.matchMedia(
    '(prefers-reduced-motion: reduce)'
  ).matches;

  /*
    AUTOPLAY MODES
    "once" plays through once.
    "loop" loops while the hero is visible.
  */
  if (mode === 'once' || mode === 'loop') {
    root.style.setProperty('--jwl-progress', '0');
    root.style.setProperty('--jwl-shine-x', '45%');

    if (!video) return;

    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.loop = mode === 'loop';

    video.setAttribute('muted', '');
    video.setAttribute('playsinline', '');

    /*
      Respect reduced-motion preferences by leaving
      the video paused on its poster/first frame.
    */
    if (reduceMotion) {
      video.pause();
      return;
    }

    let videoHasEnded = false;

    video.addEventListener('ended', function () {
      if (mode === 'once') {
        videoHasEnded = true;
      }
    });

    const playVideo = function () {
      if (mode === 'once' && videoHasEnded) return;

      const playPromise = video.play();

      if (
        playPromise &&
        typeof playPromise.catch === 'function'
      ) {
        playPromise.catch(function () {
          /*
            The video remains visible if the browser
            unexpectedly blocks autoplay.
          */
        });
      }
    };

    const pauseVideo = function () {
      if (!video.paused) {
        video.pause();
      }
    };

    if (!('IntersectionObserver' in window)) {
      playVideo();
      return;
    }

    const videoObserver = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            playVideo();
          } else {
            /*
              Pause off-screen for performance.
              "Once" resumes from the same point when
              the customer returns; it does not restart.
            */
            pauseVideo();
          }
        });
      },
      {
        threshold: 0.35
      }
    );

    videoObserver.observe(story);
    return;
  }

  /*
    EXISTING SCROLL-CONTROLLED MODE
  */
  if (reduceMotion) return;

  let duration = 0;
  let ticking = false;

  if (video) {
    video.muted = true;
    video.defaultMuted = true;
    video.playsInline = true;
    video.autoplay = false;
    video.loop = false;
    video.pause();
  }

  const update = function () {
    ticking = false;

    const rect = story.getBoundingClientRect();

    const scrollable = Math.max(
      1,
      story.offsetHeight - window.innerHeight
    );

    const progress = clamp(-rect.top / scrollable);

    root.style.setProperty(
      '--jwl-progress',
      progress.toFixed(4)
    );

    root.style.setProperty(
      '--jwl-shine-x',
      `${-15 + progress * 130}%`
    );

    const stepIndex =
      progress < 0.34 ? 0 :
      progress < 0.68 ? 1 :
      2;

    steps.forEach(function (step, index) {
      step.classList.toggle(
        'is-active',
        index === stepIndex
      );
    });

    if (
      video &&
      duration > 0 &&
      Number.isFinite(duration)
    ) {
      const targetTime = clamp(
        progress * duration,
        0,
        Math.max(0, duration - 0.03)
      );

      if (
        Math.abs(video.currentTime - targetTime) > 0.025
      ) {
        try {
          video.currentTime = targetTime;
        } catch (error) {
          /*
            Seeking can briefly fail before video
            metadata is fully available.
          */
        }
      }
    }
  };

  const requestUpdate = function () {
    if (ticking) return;

    ticking = true;
    window.requestAnimationFrame(update);
  };

  if (video) {
    const captureDuration = function () {
      duration = video.duration || 0;
      requestUpdate();
    };

    if (video.readyState >= 1) {
      captureDuration();
    } else {
      video.addEventListener(
        'loadedmetadata',
        captureDuration,
        { once: true }
      );
    }
  }

  window.addEventListener(
    'scroll',
    requestUpdate,
    { passive: true }
  );

  window.addEventListener('resize', requestUpdate);

  requestUpdate();
}

  function initReveals(root) {
    const elements = root.querySelectorAll('.jwl-reveal');
    if (!elements.length) return;

    if (!('IntersectionObserver' in window)) {
      elements.forEach((element) => element.classList.add('is-visible'));
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.14, rootMargin: '0px 0px -6% 0px' });

    elements.forEach((element) => observer.observe(element));
  }

  function initGallery(root) {
    const mainImage = root.querySelector('[data-jwl-main-image]');
    const thumbs = [...root.querySelectorAll('[data-jwl-thumb]')];
    if (!mainImage || !thumbs.length) return;

    const showThumb = (thumb) => {
      const url = thumb.dataset.imageUrl;
      if (!url) return;

      mainImage.style.opacity = '0.25';
      const preload = new Image();
      preload.onload = () => {
        mainImage.removeAttribute('srcset');
        mainImage.src = url;
        mainImage.style.opacity = '1';
      };
      preload.onerror = () => { mainImage.style.opacity = '1'; };
      preload.src = url;

      thumbs.forEach((item) => item.classList.toggle('is-active', item === thumb));
    };

    thumbs.forEach((thumb) => thumb.addEventListener('click', () => showThumb(thumb)));

    return {
      showMedia(mediaId) {
        if (!mediaId) return;
        const thumb = thumbs.find((item) => String(item.dataset.mediaId) === String(mediaId));
        if (thumb) showThumb(thumb);
      }
    };
  }

  function initProduct(root, gallery) {
    const variantsScript = root.querySelector('[data-jwl-variants]');
    const form = root.querySelector('.jwl-product-form');
    if (!variantsScript || !form) return;

    let variants = [];
    try {
      variants = JSON.parse(variantsScript.textContent);
    } catch (error) {
      return;
    }

    const optionGroups = [...form.querySelectorAll('[data-jwl-option]')];
    const variantInput = form.querySelector('[data-jwl-variant-id]');
    const prices = [...root.querySelectorAll('[data-jwl-price]')];
const comparePrices = [
  ...root.querySelectorAll('[data-jwl-compare-price]')
];
    const addButton = form.querySelector('[data-jwl-add-button]');
    const addLabel = form.querySelector('[data-jwl-add-label]');
    const quantity = form.querySelector('[data-jwl-quantity]');
    const currency = root.dataset.currency || 'GBP';

const engravingCard = form.querySelector('[data-jwl-eng-card]');

if (engravingCard) {
  const toggle = engravingCard.querySelector(
    '[data-jwl-eng-toggle]'
  );

  const field = engravingCard.querySelector(
    '[data-jwl-eng-field]'
  );

  const textInput = engravingCard.querySelector(
    '[data-jwl-eng-text]'
  );

  const counter = engravingCard.querySelector(
    '[data-jwl-eng-count]'
  );

  const errorMessage = engravingCard.querySelector(
    '[data-jwl-eng-error]'
  );

  const textProperty = engravingCard.querySelector(
    '[data-jwl-eng-property-text]'
  );

  const feeProperty = engravingCard.querySelector(
    '[data-jwl-eng-property-fee]'
  );

  const clearError = () => {
    textInput.classList.remove('has-error');

    if (errorMessage) {
      errorMessage.hidden = true;
    }
  };

  const syncEngraving = () => {
    const engravingText = textInput.value.trim();

    const engravingIsValid =
      toggle.checked && engravingText.length > 0;

    textProperty.value = engravingText;
    textProperty.disabled = !engravingIsValid;
    feeProperty.disabled = !engravingIsValid;

    if (counter) {
      counter.textContent =
        `${textInput.value.length}/${textInput.maxLength}`;
    }
  };

  toggle.addEventListener('change', () => {
    const isOpen = toggle.checked;

    field.classList.toggle('is-open', isOpen);
    toggle.setAttribute('aria-expanded', String(isOpen));
    textInput.disabled = !isOpen;

    if (isOpen) {
      window.setTimeout(() => {
        textInput.focus();
      }, 220);
    } else {
      textInput.value = '';
      clearError();
    }

    syncEngraving();
  });

  textInput.addEventListener('input', () => {
    clearError();
    syncEngraving();
  });

  form.addEventListener('submit', (event) => {
    if (
      toggle.checked &&
      textInput.value.trim().length === 0
    ) {
      event.preventDefault();
      event.stopPropagation();

      textInput.classList.add('has-error');

      if (errorMessage) {
        errorMessage.hidden = false;
      }

      textInput.focus();
      return;
    }

    syncEngraving();
  });

  syncEngraving();
}

    const selectedOptions = () => optionGroups.map((group) => {
      const checked = group.querySelector('[data-jwl-option-value]:checked');
      return checked ? checked.value : null;
    });

    const updateVariant = () => {
      const options = selectedOptions();
      const variant = variants.find((item) => item.options.every((option, index) => option === options[index]));

      if (!variant) {
        if (variantInput) variantInput.value = '';
        if (addButton) addButton.disabled = true;
        if (addLabel) addLabel.textContent = 'Unavailable';
        return;
      }

      if (variantInput) variantInput.value = variant.id;
      prices.forEach((priceElement) => {
  priceElement.textContent = formatMoney(
    variant.price,
    currency
  );
});

comparePrices.forEach((comparePriceElement) => {
  const hasCompare =
    Number(variant.compare_at_price) > Number(variant.price);

  comparePriceElement.hidden = !hasCompare;

  comparePriceElement.textContent = hasCompare
    ? formatMoney(variant.compare_at_price, currency)
    : '';
});

      if (addButton) addButton.disabled = !variant.available;
      if (addLabel) addLabel.textContent = variant.available ? 'Add to cart' : 'Sold out';

      const mediaId = variant.featured_media && variant.featured_media.id;
      if (gallery && mediaId) gallery.showMedia(mediaId);

      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    };

   form.querySelectorAll('[data-jwl-option-value]').forEach((input) => {
  input.addEventListener('change', () => {
    const optionGroup = input.closest('[data-jwl-option]');
    const currentLabel = optionGroup?.querySelector(
      '[data-jwl-option-current]'
    );

    if (currentLabel) {
      currentLabel.textContent = input.value;
    }

    updateVariant();
  });
});

    const minus = form.querySelector('[data-jwl-quantity-minus]');
    const plus = form.querySelector('[data-jwl-quantity-plus]');

    if (minus && quantity) {
      minus.addEventListener('click', () => {
        quantity.value = Math.max(1, Number(quantity.value || 1) - 1);
      });
    }

    if (plus && quantity) {
      plus.addEventListener('click', () => {
        quantity.value = Math.max(1, Number(quantity.value || 1) + 1);
      });
    }
  }

  function initSection(root) {
    if (!root || root.dataset.jwlInitialized === 'true') return;
    root.dataset.jwlInitialized = 'true';

    initScrollStory(root);
    initReveals(root);
    const gallery = initGallery(root);
    initProduct(root, gallery);
  }

  function initAll(scope = document) {
    scope.querySelectorAll('[data-jwl-birthstone]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
/* =========================================================
   LUXURY SCROLL REVEALS
========================================================= */

(function () {
  function prepareTarget(element, className, delay) {
    if (!element || element.dataset.jwlLuxReady === 'true') return;

    element.classList.add(className);
    element.style.setProperty('--jwl-lux-delay', delay + 'ms');
    element.dataset.jwlLuxReady = 'true';
  }

  function initialiseLuxuryReveals(root) {
    var page = null;

    if (
      root.classList &&
      root.classList.contains('jwl-birthstone')
    ) {
      page = root;
    } else if (root.querySelector) {
      page = root.querySelector('.jwl-birthstone');
    }

    if (!page) return;

    /*
      Add staggered typography animation to the main sections.
      The hero is intentionally excluded.
    */
    var sections = page.querySelectorAll(
      [
        '.jwl-quick-shop',
        '.jwl-meaning',
        '.jwl-gift-reveal',
        '.jwl-reasons',
        '.jwl-purchase'
      ].join(',')
    );

    sections.forEach(function (section) {
      var kicker = section.querySelector('.jwl-kicker');

      var titles = section.querySelectorAll(
  [
    'h1',
    'h2',
    'h3',
    '.jwl-buybox__title'
  ].join(',')
);

      var copy = section.querySelector(
        [
          '.jwl-quick-shop__text',
          '.jwl-gift-reveal__text',
          '.jwl-buybox__summary',
          '.rte'
        ].join(',')
      );

      prepareTarget(kicker, 'jwl-lux-copy-reveal', 0);
      titles.forEach(function (title, index) {
  prepareTarget(
    title,
    'jwl-lux-title-reveal',
    70 + index * 80
  );
});
      prepareTarget(copy, 'jwl-lux-copy-reveal', 170);
    });

    /*
      Animate only selected important images.
      Avoid animating every thumbnail or decorative asset.
    */
    var images = page.querySelectorAll(
      [
        '.jwl-quick-shop__image',
        '.jwl-meaning .jwl-collage__image',
        '.jwl-gift-reveal__image',
        '.jwl-benefit__image-element',
        '.jwl-gallery__main-image'
      ].join(',')
    );

    images.forEach(function (image, index) {
      var delay = (index % 2) * 90;
      prepareTarget(image, 'jwl-lux-image-reveal', delay);
    });

    var targets = page.querySelectorAll(
      [
        '.jwl-lux-copy-reveal',
        '.jwl-lux-title-reveal',
        '.jwl-lux-image-reveal'
      ].join(',')
    );

    var reduceMotion = window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;

    if (reduceMotion || !('IntersectionObserver' in window)) {
      targets.forEach(function (target) {
        target.classList.add('is-in-view');
      });

      return;
    }

    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-in-view');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.12,
        rootMargin: '0px 0px -10% 0px'
      }
    );

    targets.forEach(function (target) {
      if (!target.classList.contains('is-in-view')) {
        observer.observe(target);
      }
    });
  }

  function startLuxuryReveals() {
    initialiseLuxuryReveals(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startLuxuryReveals
    );
  } else {
    startLuxuryReveals();
  }

  /*
    Reinitialise when Shopify reloads the section
    inside the theme customiser.
  */
  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initialiseLuxuryReveals(event.target);
    }
  );
})();
/* =========================================================
   PRICE + KLARNA VARIANT UPDATES
========================================================= */

(function () {
  function formatMoney(cents) {
    try {
      return new Intl.NumberFormat('en-GB', {
        style: 'currency',
        currency: 'GBP'
      }).format(cents / 100);
    } catch (error) {
      return '£' + (cents / 100).toFixed(2);
    }
  }

  function initialisePriceKlarna(root) {
    var wrappers = root.querySelectorAll
      ? root.querySelectorAll('[data-jwl-price-klarna]')
      : [];

    wrappers.forEach(function (wrapper) {
      if (wrapper.dataset.jwlKlarnaReady === 'true') {
        return;
      }

      var buybox = wrapper.closest('.jwl-buybox');

      if (!buybox) return;

      var form = buybox.querySelector('form');
      var variantsElement = buybox.querySelector(
        '[data-jwl-price-klarna-variants]'
      );

      var priceElement = wrapper.querySelector(
        '[data-jwl-product-price]'
      );

      var compareElement = wrapper.querySelector(
        '[data-jwl-compare-price]'
      );

      var klarnaElement = wrapper.querySelector(
        '[data-jwl-klarna-amount]'
      );

      if (
        !form ||
        !variantsElement ||
        !priceElement ||
        !compareElement ||
        !klarnaElement
      ) {
        return;
      }

      var variants = [];

      try {
        variants = JSON.parse(variantsElement.textContent);
      } catch (error) {
        return;
      }

      function getSelectedOptions() {
        return Array.from(
          form.querySelectorAll('[data-jwl-option]')
        ).map(function (optionGroup) {
          var selectedInput = optionGroup.querySelector(
            '[data-jwl-option-value]:checked'
          );

          return selectedInput ? selectedInput.value : '';
        });
      }

      function findSelectedVariant() {
        var selectedOptions = getSelectedOptions();

        return variants.find(function (variant) {
          return variant.options.every(function (
            optionValue,
            optionIndex
          ) {
            return (
              String(optionValue) ===
              String(selectedOptions[optionIndex])
            );
          });
        });
      }

      function updatePriceKlarna() {
        var selectedVariant = findSelectedVariant();

        if (!selectedVariant) return;

        priceElement.textContent = formatMoney(
          selectedVariant.price
        );

        klarnaElement.textContent = formatMoney(
          Math.floor(selectedVariant.price / 3)
        );

        if (
          selectedVariant.compare_at_price &&
          selectedVariant.compare_at_price >
            selectedVariant.price
        ) {
          compareElement.textContent = formatMoney(
            selectedVariant.compare_at_price
          );

          compareElement.hidden = false;
        } else {
          compareElement.textContent = '';
          compareElement.hidden = true;
        }
      }

      form.addEventListener('change', function (event) {
        if (
          event.target &&
          event.target.matches('[data-jwl-option-value]')
        ) {
          window.requestAnimationFrame(updatePriceKlarna);
        }
      });

      wrapper.dataset.jwlKlarnaReady = 'true';

      updatePriceKlarna();
    });
  }

  function startPriceKlarna() {
    initialisePriceKlarna(document);
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      startPriceKlarna
    );
  } else {
    startPriceKlarna();
  }

  document.addEventListener(
    'shopify:section:load',
    function (event) {
      initialisePriceKlarna(event.target);
    }
  );
})();
